import {
  Box,
  Flex,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useModels } from '../../hooks/models/useModels'
import { AIModel, ConfigurableParameter } from '../../types/modelTypes'
import { useLanguage } from '../../hooks/utility/useLanguage'
function localize(translations: Record<string, string> | null | undefined, language: string): string {
  if (!translations) return ''
  return translations[language] ?? translations['en'] ?? Object.values(translations)[0] ?? ''
}

interface ParameterControlProps {
  param: ConfigurableParameter
  value: number | string
  onChange: (key: string, value: number | string) => void
  textColor: string
  subtleColor: string
}

const ParameterControl: React.FC<ParameterControlProps> = ({
  param,
  value,
  onChange,
  textColor,
  subtleColor,
}) => {
  const { currentLanguage } = useLanguage()

  const localizedLabel = localize(param.label, currentLanguage)
  const localizedDescription = param.description ? localize(param.description, currentLanguage) : ''

  if (param.type === 'slider') {
    const numValue = typeof value === 'number' ? value : (param.default as number) ?? 0
    return (
      <Box w='100%'>
        <Flex justify='space-between' mb={1}>
          <Text fontSize='sm' color={textColor}>
            {localizedLabel}
          </Text>
          <Text fontSize='sm' color={subtleColor}>
            {numValue}
          </Text>
        </Flex>
        {localizedDescription && (
          <Text fontSize='xs' color={subtleColor} mb={2}>
            {localizedDescription}
          </Text>
        )}
        <Slider
          value={numValue}
          min={param.min ?? 0}
          max={param.max ?? 1}
          step={param.step ?? 0.1}
          onChange={v => onChange(param.key, v)}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </Box>
    )
  }

  if (param.type === 'select') {
    return (
      <Box w='100%'>
        <Text fontSize='sm' color={textColor} mb={1}>
          {localizedLabel}
        </Text>
        {localizedDescription && (
          <Text fontSize='xs' color={subtleColor} mb={2}>
            {localizedDescription}
          </Text>
        )}
        <Select value={String(value)} onChange={e => onChange(param.key, e.target.value)}>
          {param.options?.map(opt => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </Select>
      </Box>
    )
  }

  return null
}

interface ModelCardProps {
  model: AIModel
  settings: Record<string, number | string>
  onSettingsChange: (modelName: string, settings: Record<string, number | string>) => void
  textColor: string
  subtleColor: string
}

const ModelCard: React.FC<ModelCardProps> = ({
  model,
  settings,
  onSettingsChange,
  textColor,
  subtleColor,
}) => {
  const { t, currentLanguage } = useLanguage()
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  const [localSettings, setLocalSettings] = useState<Record<string, number | string>>({})
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const localizedDescription = model.description ? localize(model.description, currentLanguage) : ''

  useEffect(() => {
    const defaults: Record<string, number | string> = {}
    for (const param of model.configurable_parameters) {
      if (param.default !== undefined) {
        defaults[param.key] = param.default
      }
    }
    setLocalSettings({ ...defaults, ...settings })
  }, [model.configurable_parameters, settings])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const debouncedSettingsChange = useCallback(
    (updatedSettings: Record<string, number | string>) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        onSettingsChange(model.name, updatedSettings)
      }, 400)
    },
    [model.name, onSettingsChange],
  )

  const handleChange = (key: string, value: number | string) => {
    const updated = { ...localSettings, [key]: value }
    setLocalSettings(updated)
    debouncedSettingsChange(updated)
  }

  if (model.configurable_parameters.length === 0) {
    return (
      <Box bg={cardBg} p={5} borderRadius='xl' borderWidth='1px' borderColor={borderColor}>
        <Text fontSize='lg' fontWeight='medium' color={textColor} mb={2}>
          {model.display_name || model.name}
        </Text>
        {localizedDescription && (
          <Text fontSize='sm' color={subtleColor} mb={2}>
            {localizedDescription}
          </Text>
        )}
        <Text fontSize='sm' color={subtleColor}>
          {t('models.noConfigurableParameters')}
        </Text>
      </Box>
    )
  }

  return (
    <Box bg={cardBg} p={5} borderRadius='xl' borderWidth='1px' borderColor={borderColor}>
      <Text fontSize='lg' fontWeight='medium' color={textColor} mb={2}>
        {model.display_name || model.name}
      </Text>
      {localizedDescription && (
        <Text fontSize='sm' color={subtleColor} mb={4}>
          {localizedDescription}
        </Text>
      )}
      <VStack spacing={4} align='stretch'>
        {model.configurable_parameters.map(param => (
          <ParameterControl
            key={param.key}
            param={param}
            value={localSettings[param.key] ?? param.default ?? ''}
            onChange={handleChange}
            textColor={textColor}
            subtleColor={subtleColor}
          />
        ))}
      </VStack>
    </Box>
  )
}

export const ModelSettingsTab: React.FC = () => {
  const { t } = useLanguage()
  const { models, userSettings, isLoading, updateSettings, isUpdating, isUpdateError } = useModels()
  const textColor = useColorModeValue('gray.700', 'white')
  const subtleColor = useColorModeValue('gray.500', 'gray.400')
  const toast = useToast()

  useEffect(() => {
    if (isUpdateError) {
      toast({
        title: t('profile.settingsUpdateFailed'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }, [isUpdateError, toast, t])

  const handleSettingsChange = (modelName: string, settings: Record<string, number | string>) => {
    updateSettings({ modelName, settings })
  }

  if (isLoading) {
    return (
      <Flex justify='center' py={8}>
        <Spinner />
      </Flex>
    )
  }

  if (models.length === 0) {
    return (
      <Text color={subtleColor} textAlign='center' py={8}>
        {t('profile.noModelsAvailable')}
      </Text>
    )
  }

  return (
    <VStack spacing={4} align='stretch'>
      <Box>
        <Text fontSize='lg' fontWeight='semibold' color={textColor} mb={1}>
          {t('models.introHeading')}
        </Text>
        <Text fontSize='sm' color={subtleColor}>
          {t('models.introBody')}
        </Text>
      </Box>
      {isUpdating && (
        <Flex align='center' gap={2}>
          <Spinner size='xs' />
          <Text fontSize='sm' color={subtleColor}>
            {t('profile.savingSettings')}
          </Text>
        </Flex>
      )}
      {models.map(model => (
        <ModelCard
          key={model.name}
          model={model}
          settings={userSettings[model.name]?.settings ?? {}}
          onSettingsChange={handleSettingsChange}
          textColor={textColor}
          subtleColor={subtleColor}
        />
      ))}
    </VStack>
  )
}
